/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Box, Icon } from '@hover/blueprint';
import {
  iFileText,
  iHome,
  iUser,
  hUserBusiness,
  hUserConstruction,
  iShoppingCart,
} from '@hover/icons';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import type { IHomePageCaseStudy } from '../interfaces/IHomePageCaseStudy';
import type { IHomePageTabs } from '../interfaces/IHomePageTabs';
import type { ICard } from '../interfaces/IHomePageWelcome';
import type { ILearningCardProps } from '../components/common/learning-card';

import EstimatesPlaceholder from '../assets/images/estimates_placeholder.png';
import MeasurementPlaceholder from '../assets/images/measurements_placeholder.png';
import OrderingPlaceholder from '../assets/images/ordering_placeholder.png';
import VisualizationPlaceholder from '../assets/images/visualization_placeholder.png';
import HomePageCaseStudy1 from '../assets/svgs/HomePageCaseStudy1';
import HomePageCaseStudy2 from '../assets/svgs/HomePageCaseStudy2';
import HomePageCaseStudy3 from '../assets/svgs/HomePageCaseStudy3';
import MeasureIconActive from '../assets/svgs/MeasureIconActive';
import HomePageEstimates from '../assets/videos/home_page_estimates.mp4';
import HomePageEstimatesWebm from '../assets/videos/home_page_estimates.webm';
import HomePageMeasurements from '../assets/videos/home_page_measurement.mp4';
import HomePageMeasurementsWebm from '../assets/videos/home_page_measurement.webm';
import HomePageOrdering from '../assets/videos/home_page_ordering.mp4';
import HomePageOrderingWebm from '../assets/videos/home_page_ordering.webm';
import HomePageVisualization from '../assets/videos/home_page_visualization.mp4';
import HomePageVisualizationWebm from '../assets/videos/home_page_visualization.webm';

export const HOME_PAGE_SIGN_UP_CARDS: ICard[] = [
  {
    title: "I'm a homeowner",
    description: "I'll be using HOVER for projects on my property.",
    linkTo: '/signup/homeowner',
    nativeLink: true,
    icon: <Icon icon={iUser} size="huge" color="primary.600" />,
  },
  {
    title: "I'm a builder or home\u00A0pro",
    description: "I'll be using HOVER for my work or business.",
    linkTo: '/signup/professional',
    nativeLink: true,
    icon: <Icon icon={hUserConstruction} size="huge" color="primary.600" />,
  },
  {
    title: "I'm an insurance pro",
    description: "I'll be using HOVER for adjustments and claims.",
    linkTo: '/signup/insurance',
    nativeLink: true,
    icon: <Icon icon={hUserBusiness} size="huge" color="primary.600" />,
  },
];

export const HOME_PAGE_BETTER_FOR_EVERYONE: ILearningCardProps['card'][] = [
  {
    id: 1,
    description:
      'Visualize your project, collaborate on the plan and know the costs, all before you get started.',
    title: 'Homeowners',
    bgColor: 'warning.50',
    titleColor: 'brandGreen.500',
    linkTitle: 'Design here',
    responsiveLinkTitle: 'Read more',
    linkTo: '/how-it-works/',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="home page homeowners"
        src="../assets/images/home_page_owners.png"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="home page homeowners"
        src="../assets/images/home_page_owners_mobile.png"
      />
    ),
  },
  {
    id: 2,
    description:
      'Get measurements, complete estimates and order materials, all in one place.',
    title: 'Pros',
    bgColor: 'warning.50',
    linkTitle: 'Build here',
    titleColor: 'brandOrange.500',
    linkTo: '/exterior-contractors/',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="home page pros"
        src="../assets/images/home_page_pros.png"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="home page pros"
        src="../assets/images/home_page_pros_mobile.png"
      />
    ),
  },
  {
    id: 3,
    description:
      'A quicker claims process makes one of the hardest things anyone goes through a little bit easier.',
    title: 'Insurance',
    linkTitle: 'The process',
    bgColor: 'warning.50',
    titleColor: 'brown.500',
    linkTo: '/insurance/',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="home page insurance"
        src="../assets/images/home_page_insurance.png"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="home page insurance"
        src="../assets/images/home_page_insurance_mobile.png"
      />
    ),
  },
  {
    id: 4,
    description:
      'Spend less time taking and filling orders and more time building relationships.',
    title: 'Distributors',
    linkTitle: 'Meet our partners',
    linkTo: '/partners/',
    nativeLink: true,
    bgColor: 'warning.50',
    titleColor: 'brandNavy.500',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="home page distributors"
        src="../assets/images/home_page_distributors.png"
        layout="constrained"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="home page distributors"
        src="../assets/images/home_page_distributors_mobile.png"
        objectFit="contain"
      />
    ),
  },
];

export const HOME_PAGE_CASE_STUDIES: IHomePageCaseStudy[] = [
  {
    id: 1,
    description:
      'The 3D model is way more effective at visualizing what the homeowner’s house will really look like in the future. It gives homeowners the confidence.',
    name: 'Caleb Mitchell',
    role: 'BELDON—San antonio, TX',
    image: (
      <Box
        w={{ base: '271px', desktop: '378px' }}
        h={{ base: '298px', desktop: '411px' }}
        position="relative"
      >
        <Box
          w={{ base: '90%', desktop: 'auto' }}
          position="absolute"
          top="0"
          left="0"
          aria-hidden
        >
          <HomePageCaseStudy1 />
        </Box>
        <Box position="absolute" top="35px" left="35px">
          <StaticImage
            placeholder="blurred"
            objectFit="contain"
            alt="case study"
            src="../assets/images/home_page_case_study_1.png"
          />
        </Box>
      </Box>
    ),
  },
  {
    id: 2,
    description:
      'With HOVER, we can provide homeowners with everything they need to be in control of their remodeling project and confidently make decisions. The homeowner knows exactly what to expect from the very start.',
    name: 'Chris Falasco',
    role: 'Endeavor Exteriors—Denver, CO',
    image: (
      <Box
        w={{ base: '271px', desktop: '378px' }}
        h={{ base: '298px', desktop: '411px' }}
        position="relative"
      >
        <Box
          w={{ base: '90%', desktop: 'auto' }}
          position="absolute"
          top="0"
          left="0"
          aria-hidden
        >
          <HomePageCaseStudy2 />
        </Box>
        <Box position="absolute" top="35px" left="35px">
          <StaticImage
            placeholder="blurred"
            objectFit="contain"
            alt="case study"
            src="../assets/images/home_page_case_study_2.png"
          />
        </Box>
      </Box>
    ),
  },
  {
    id: 3,
    description:
      'There has not been one time that I felt like I had to go back and re-measure a job after HOVER. I would trust a HOVER report over any project manager—including myself.',
    name: 'Bill Kassakatis',
    role: 'Hometown Exteriors—Baltimore, MD',
    image: (
      <Box
        w={{ base: '271px', desktop: '378px' }}
        h={{ base: '298px', desktop: '411px' }}
        position="relative"
      >
        <Box
          w={{ base: '90%', desktop: 'auto' }}
          position="absolute"
          top="0"
          left="0"
          aria-hidden
        >
          <HomePageCaseStudy3 />
        </Box>
        <Box position="absolute" top="35px" left="35px">
          <StaticImage
            placeholder="blurred"
            objectFit="contain"
            alt="case study"
            src="../assets/images/home_page_case_study_3.png"
          />
        </Box>
      </Box>
    ),
  },
];

export const HOME_PAGE_TABS: IHomePageTabs[] = [
  {
    id: 0,
    title: 'Spot-ON Measurements',
    body: 'Turn photos from your phone into fast and accurate measurements that save time and money.',
    svgIcon: <MeasureIconActive aria-hidden />,
    // TODO remove ts-ignore
    // @ts-ignore
    renderVideo(
      props: JSX.IntrinsicAttributes &
        React.ClassAttributes<HTMLVideoElement> &
        React.VideoHTMLAttributes<HTMLVideoElement>,
    ) {
      return (
        <video muted {...props} playsInline poster={MeasurementPlaceholder}>
          <source src={HomePageMeasurements} type='video/mp4; codecs="hvc1"' />
          <source src={HomePageMeasurementsWebm} type="video/webm" />
        </video>
      );
    },
  },
  {
    id: 1,
    title: 'Visualize In 3d',
    body: 'Collaborative 3D models let you visualize your project before you get started.',
    icon: iHome,
    // TODO remove ts-ignore
    // @ts-ignore
    renderVideo(
      props: JSX.IntrinsicAttributes &
        React.ClassAttributes<HTMLVideoElement> &
        React.VideoHTMLAttributes<HTMLVideoElement>,
    ) {
      return (
        <video muted {...props} playsInline poster={VisualizationPlaceholder}>
          <source src={HomePageVisualization} type='video/mp4; codecs="hvc1"' />
          <source src={HomePageVisualizationWebm} type="video/webm" />
        </video>
      );
    },
  },
  {
    id: 2,
    title: 'BETTER ESTIMATES',
    body: 'Faster and more accurate measurements give you faster and more accurate estimates.',
    icon: iFileText,
    // TODO remove ts-ignore
    // @ts-ignore
    renderVideo(
      props: JSX.IntrinsicAttributes &
        React.ClassAttributes<HTMLVideoElement> &
        React.VideoHTMLAttributes<HTMLVideoElement>,
    ) {
      return (
        <video muted {...props} playsInline poster={EstimatesPlaceholder}>
          <source src={HomePageEstimates} type='video/mp4; codecs="hvc1"' />
          <source src={HomePageEstimatesWebm} type="video/webm" />
        </video>
      );
    },
  },
  {
    id: 3,
    title: 'Order Directly',
    body: 'Seamlessly order exactly the right amount of materials for every project right inside the app.',
    icon: iShoppingCart,
    // TODO remove ts-ignore
    // @ts-ignore
    renderVideo(
      props: JSX.IntrinsicAttributes &
        React.ClassAttributes<HTMLVideoElement> &
        React.VideoHTMLAttributes<HTMLVideoElement>,
    ) {
      return (
        <video muted {...props} playsInline poster={OrderingPlaceholder}>
          <source src={HomePageOrdering} type='video/mp4; codecs="hvc1"' />
          <source src={HomePageOrderingWebm} type="video/webm" />
        </video>
      );
    },
  },
];
